import axios from "axios";
import { useEffect, useState } from "react";
import { FaAngleDown } from "react-icons/fa";
import { MdOutlinePhone } from "react-icons/md";
import LoaderSpiner from "../LoaderSpiner";
import styles from "./Dashboard.module.css";

const OutboundCallDashboard = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [agents, setAgents] = useState([]);
  const [selectedCaller, setSelectedCaller] = useState([]);
  const [showSelection, setShowSelection] = useState(false);
  const [showAgentTemplate, setShowAgentTemplate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [webHookApi, setWebHookApi] = useState("");
  const [webHookApiData, setWebHookApiData] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [successfulCalls, setSuccessfulCalls] = useState(() => {
    const saved = localStorage.getItem("successfulCalls");
    return saved ? JSON.parse(saved) : [];
  });
  const [callResults, setCallResults] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [webhookSampleData, setWebhookSampleData] = useState(null);
  const [webhookMapping, setWebhookMapping] = useState({});
  const token = localStorage.getItem("token");
  const userID = localStorage.getItem("userId");

  // Required fields for mapping
  const requiredCallFields = [
    { key: "number", label: "Phone Number to Call", required: true },
    { key: "customerName", label: "Customer Name", required: false },
  ];

  useEffect(() => {
    localStorage.setItem("successfulCalls", JSON.stringify(successfulCalls));
  }, [successfulCalls]);

  useEffect(() => {
    const fetchAgentData = async () => {
      if (!userID || !token) return;
      try {
        setIsLoading(true);
        const response = await fetch(
          `https://prod.toxboxai.com/api/new/agent/user/${userID}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!response.ok) throw new Error(`Error: ${response.status}`);
        const result = await response.json();
        setAgents(result.data || []);
      } catch (error) {
        console.error("Error fetching agent data:", error);
        setAgents([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAgentData();
  }, [userID, token]);

  useEffect(() => {
    if (agents.length > 0 && !selectedAgent) {
      const firstAgent = agents[0];
      if (firstAgent?.webhook) {
        setSelectedAgent(firstAgent);
        setWebHookApi(firstAgent.webhook);
        setWebhookMapping(firstAgent.webhookMapping || {});
      } else {
        console.warn("First agent has no valid webhook URL");
      }
    }
  }, [agents, selectedAgent]);

  useEffect(() => {
    const fetchAndProcessWebhookData = async () => {
      if (!webHookApi || !selectedAgent) return;
      try {
        const response = await axios.get(webHookApi);
        const responseData = Array.isArray(response.data)
          ? response.data
          : Object.values(response.data);

        const scriptTemplate = selectedAgent.generatedScript || "";
        const script = scriptTemplate.replace(
          /\{\{(.*?)\}\}/g,
          (_, match) => `\${user.${match.trim()}}`
        );

        const results = responseData
          .map((user, index) => {
            try {
              const generatedScript = new Function(
                "user",
                `return \`${script}\`;`
              )(user);
              const callObj = {
                id: user.id || index,
                user, // Include full user data
                ...selectedAgent,
                number: webhookMapping.number
                  ? user[webhookMapping.number]
                  : user.phoneNumber || "",
                customerName: webhookMapping.customerName
                  ? user[webhookMapping.customerName]
                  : user.customerName || "",
                first_message: generatedScript,
                userPrompt: selectedAgent.commandyouragent || "",
                agent_id: selectedAgent.elevenLabsAgentId || "",
                fromNumber: selectedAgent.phoneNumber || "",
                agent_languages: ["hindi", "english"],
                agentName: selectedAgent.name || "Unnamed Agent",
              };
              if (!callObj.number) {
                console.warn(
                  `Skipping user ${index}: No valid phone number found`
                );
                return null;
              }
              return callObj;
            } catch (error) {
              console.error(`Error processing user ${index}:`, error);
              return null;
            }
          })
          .filter((item) => item !== null);

        setWebHookApiData(results);
      } catch (error) {
        console.error("Error fetching or processing webhook data:", error);
        setWebHookApiData([]);
      }
    };
    fetchAndProcessWebhookData();
  }, [webHookApi, selectedAgent, webhookMapping]);

  const handleCallApi = async (url, agent) => {
    setWebHookApi(url);
    setSelectedAgent(agent);
    setWebhookMapping(agent.webhookMapping || {});

    // Fetch sample webhook data to prompt for mapping if no mapping exists for number
    if (!agent.webhookMapping || !agent.webhookMapping.number) {
      try {
        const response = await axios.get(url);
        const sampleData =
          Array.isArray(response.data) && response.data.length > 0
            ? response.data[0]
            : Object.values(response.data)[0] || {};
        setWebhookSampleData(sampleData);
        setShowMappingModal(true);
      } catch (error) {
        console.error("Error fetching webhook sample data:", error);
        alert("Failed to fetch webhook data for mapping. Please try again.");
      }
    }
  };

  const handleMappingSubmit = async () => {
    // Validate required field
    if (!webhookMapping.number) {
      alert("Please map the Phone Number to Call field.");
      return;
    }

    // Save mapping to the agent in the backend
    try {
      await axios.put(
        `https://prod.toxboxai.com/api/new/agent/${selectedAgent._id}`,
        {
          webhookMapping,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setShowMappingModal(false);
      setWebhookSampleData(null);
    } catch (error) {
      console.error("Error saving webhook mapping:", error);
      alert("Failed to save webhook mapping. Please try again.");
    }
  };

  const handleMappingChange = (callField, webhookField) => {
    setWebhookMapping((prev) => ({
      ...prev,
      [callField]: webhookField,
    }));
  };

  const filteredData = webHookApiData.filter((item) => {
    const searchText = searchQuery.toLowerCase();
    const dataToSearch = item.user || item;

    return Object.values(dataToSearch).some((value) => {
      if (value === null || value === undefined) return false;
      if (typeof value === "object") {
        return Object.values(value).some(
          (nestedValue) =>
            nestedValue &&
            nestedValue.toString().toLowerCase().includes(searchText)
        );
      }
      return value.toString().toLowerCase().includes(searchText);
    });
  });

  const handleTabClick = (tab) => setActiveTab(tab);

  const handleCheckboxChange = (contact) => {
    setSelectedCaller((prev) => {
      const isSelected = prev.some((c) => c.id === contact.id);
      return isSelected
        ? prev.filter((c) => c.id !== contact.id)
        : [...prev, contact];
    });
  };

  const handleSelect = (count) => {
    const contactsToSelect =
      count === "all" ? [...webHookApiData] : webHookApiData.slice(0, count);
    setSelectedCaller(contactsToSelect);
    setShowSelection(false);
  };

  const initiateCall = async (calls) => {
    try {
      // Determine the endpoint based on fromNumber
      const fromNumber = Array.isArray(calls)
        ? calls[0]?.fromNumber
        : calls.fromNumber;
      const endpoint =
        fromNumber && fromNumber.startsWith("+91")
          ? "https://65.0.117.60:8080/make-call"
          : "https://65.0.117.60:8080/make-call";

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ calls }),
      });

      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);

      const data = await response.json();
      const newResults = {};

      if (data.successes && data.successes.length > 0) {
        const newSuccessfulCalls = Array.isArray(calls) ? calls : [calls];
        setSuccessfulCalls((prev) => [
          ...prev,
          ...newSuccessfulCalls.map((call) => ({
            ...call,
            timestamp: new Date().toISOString(),
            apiId: data.successes.find((s) => s.requestUuid)?.apiId,
          })),
        ]);

        setWebHookApiData((prev) =>
          prev.filter(
            (item) => !newSuccessfulCalls.some((call) => call.id === item.id)
          )
        );

        setSelectedCaller((prev) =>
          prev.filter(
            (item) => !newSuccessfulCalls.some((call) => call.id === item.id)
          )
        );

        newSuccessfulCalls.forEach((call) => {
          newResults[call.number] = {
            success: true,
            apiId: data.successes.find((s) => s.requestUuid)?.apiId,
            timestamp: new Date().toISOString(),
          };
        });
      }

      if (data.failures && data.failures.length > 0) {
        const failedCalls = Array.isArray(calls) ? calls : [calls];
        failedCalls.forEach((call) => {
          newResults[call.number] = {
            success: false,
            timestamp: new Date().toISOString(),
          };
        });
      }

      setCallResults((prev) => ({ ...prev, ...newResults }));
      return data;
    } catch (error) {
      console.error("Error initiating call:", error);
      return null;
    }
  };

  const handleIndividualCall = async (item) => {
    const calls = [
      {
        ...item,
        userPrompt: item.userPrompt,
        agent_languages: ["hindi", "english"],
        agent_name: item.agent_name,
      },
    ];
    await initiateCall(calls);
  };

  const handleDynamicCall = async () => {
    const isAllSelected =
      selectedCaller.length === webHookApiData.length &&
      webHookApiData.length > 0;

    if (isAllSelected) {
      await handleCallAll();
    } else {
      await handleCallSelected();
    }
  };

  const handleCallAll = async () => {
    const calls = webHookApiData.map((item) => ({
      ...item,
      userPrompt: item.userPrompt,
      agent_languages: ["hindi", "english"],
      agent_name: item.agent_name,
    }));
    await initiateCall(calls);
  };

  const handleCallSelected = async () => {
    const calls = selectedCaller.map((item) => ({
      ...item,
      userPrompt: item.userPrompt,
      agent_languages: ["hindi", "english"],
      agent_name: item.agent_name,
    }));
    await initiateCall(calls);
  };

  return (
    <div className={styles.container}>
      {showMappingModal && webhookSampleData && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h2>Map Webhook Fields</h2>
            <p>
              Please select the webhook fields for the phone number and customer
              name.
            </p>
            {requiredCallFields.map((field) => (
              <div key={field.key} className={styles.mappingRow}>
                <label>
                  {field.label} {field.required && <span>*</span>}
                </label>
                <select
                  value={webhookMapping[field.key] || ""}
                  onChange={(e) =>
                    handleMappingChange(field.key, e.target.value)
                  }
                >
                  <option value="">Select a field</option>
                  {Object.keys(webhookSampleData).map((key) => (
                    <option key={key} value={key}>
                      {key}
                    </option>
                  ))}
                </select>
              </div>
            ))}
            <div className={styles.modalButtons}>
              <button onClick={() => setShowMappingModal(false)}>Cancel</button>
              <button onClick={handleMappingSubmit}>Save Mapping</button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.header}>
        <input
          type="text"
          placeholder="Search"
          className={styles.search}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <button
          className={styles.dropdown}
          onClick={() => setShowAgentTemplate(!showAgentTemplate)}
        >
          {selectedAgent ? selectedAgent.name : "Select Avatar"} <FaAngleDown />
          {showAgentTemplate && (
            <div className={styles.selectTemplateContainer}>
              {agents.map((agent) => (
                <div key={agent._id}>
                  <p onClick={() => handleCallApi(agent.webhook, agent)}>
                    {agent.name}{" "}
                    {agent.webhook && <span>(Webhook Available)</span>}
                  </p>
                </div>
              ))}
            </div>
          )}
        </button>
        <button
          className={styles.button}
          onClick={handleDynamicCall}
          disabled={webHookApiData.length === 0}
        >
          {selectedCaller.length === webHookApiData.length &&
            webHookApiData.length > 0
            ? "Call All"
            : `Call Selected (${selectedCaller.length})`}
          <MdOutlinePhone />
        </button>
      </div>

      <div className={styles.tableContainer}>
        {isLoading && <LoaderSpiner />}
        {agents.length === 0 && !isLoading && <p>No agents available.</p>}
        {webHookApi &&
          activeTab !== "successful" &&
          filteredData.length > 0 && (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}></th>
                  {filteredData.length > 0 &&
                    Object.keys(filteredData[0].user || filteredData[0]).map(
                      (key) => (
                        <th key={key} className={styles.th}>
                          {key}
                        </th>
                      )
                    )}
                  <th className={styles.th}>Action</th>
                  <th className={styles.th}>Call Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item, index) => (
                  <tr key={item.id || index}>
                    <td className={styles.td}>
                      <label className={styles.checkboxContainer}>
                        <input
                          type="checkbox"
                          checked={selectedCaller.some((c) => c.id === item.id)}
                          onChange={() => handleCheckboxChange(item)}
                        />
                      </label>
                    </td>
                    {Object.keys(item.user || item).map((key) => (
                      <td key={key} className={styles.td}>
                        {typeof (item.user ? item.user[key] : item[key]) ===
                          "object" &&
                          (item.user ? item.user[key] : item[key]) !== null
                          ? Object.values(
                            item.user ? item.user[key] : item[key]
                          ).join(", ")
                          : (item.user ? item.user[key] : item[key]) ?? "N/A"}
                      </td>
                    ))}
                    <td className={styles.td}>
                      <span
                        className={styles.icon}
                        onClick={() => handleIndividualCall(item)}
                      >
                        <MdOutlinePhone />
                      </span>
                    </td>
                    <td className={styles.td}>
                      {callResults[item.number] ? (
                        callResults[item.number].success ? (
                          <span className={styles.callStatusSuccess}>
                            Call Succeeded (API ID:{" "}
                            {callResults[item.number].apiId})
                          </span>
                        ) : (
                          <span className={styles.callStatusFailed}>
                            Call Failed
                          </span>
                        )
                      ) : (
                        <span className={styles.callStatusPending}>
                          Not called
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        {webHookApi &&
          activeTab !== "successful" &&
          filteredData.length === 0 &&
          !isLoading && <p>No contacts match your search.</p>}
        {activeTab === "successful" && (
          <table className={styles.table}>
            <thead>
              <tr>
                {successfulCalls.length > 0 &&
                  Object.keys(
                    successfulCalls[0].user || successfulCalls[0]
                  ).map((key) => (
                    <th key={key} className={styles.th}>
                      {key}
                    </th>
                  ))}
                <th className={styles.th}>Action</th>
                <th className={styles.th}>Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {successfulCalls.map((item, index) => (
                <tr key={item.id || index}>
                  {Object.keys(item.user || item).map((key) => (
                    <td key={key} className={styles.td}>
                      {typeof (item.user ? item.user[key] : item[key]) ===
                        "object" &&
                        (item.user ? item.user[key] : item[key]) !== null
                        ? Object.values(
                          item.user ? item.user[key] : item[key]
                        ).join(", ")
                        : (item.user ? item.user[key] : item[key]) ?? "N/A"}
                    </td>
                  ))}
                  <td className={styles.td}>
                    <span
                      className={styles.icon}
                      onClick={() => handleIndividualCall(item)}
                    >
                      <MdOutlinePhone />
                    </span>
                  </td>
                  <td className={styles.td}>
                    {item.timestamp
                      ? new Date(item.timestamp).toLocaleString()
                      : "N/A"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className={styles.callHistory}>
        <h2>Call History</h2>
        {Object.entries(callResults).length > 0 ? (
          <ul>
            {Object.entries(callResults).map(([number, result]) => (
              <li key={number}>
                {number}: {result.success ? "Success" : "Failed"}
                {result.apiId && ` (API ID: ${result.apiId})`} -{" "}
                {new Date(result.timestamp).toLocaleString()}
              </li>
            ))}
          </ul>
        ) : (
          <p>No calls made yet</p>
        )}
      </div>
    </div>
  );
};

export default OutboundCallDashboard;
